import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRight, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/intl";

export default function InvoiceReconcileBanner({ status }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const refresh = useCallback(async () => {
    if (!status?.configured) return;
    setLoading(true);
    try {
      const r = await api.get("/invoice-sync/unmatched", {
        params: { hours: 24 },
        timeout: 30000,
      });
      setData(r.data);
    } catch (e) {
      // silent — banner just won't show
    } finally {
      setLoading(false);
    }
  }, [status?.configured]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function retry() {
    setRetrying(true);
    try {
      const r = await api.post("/invoice-sync/retry-unmatched", null, {
        params: { hours: 24 },
        timeout: 120000,
      });
      const { pushed, skipped_already_invoiced, failed = [] } = r.data || {};
      toast.success(`Retry complete · ${pushed} pushed`, {
        description: `${skipped_already_invoiced} already invoiced${
          failed.length ? `, ${failed.length} failed` : ""
        }`,
      });
      await refresh();
    } catch (e) {
      toast.error("Retry failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setRetrying(false);
    }
  }

  if (!data || data.count === 0) return null;

  const sample = (data.orders || []).slice(0, 3);
  const sample_names = sample
    .map((o) => `${o.firstName || ""} ${o.lastName || ""}`.trim() || o.email)
    .filter(Boolean)
    .join(", ");
  const sample_total = sample.reduce((a, o) => a + Number(o.total || 0), 0);
  const more = data.count - sample.length;

  return (
    <div
      data-testid="invoice-reconcile-banner"
      className="rounded-lg border border-amber-200 bg-amber-50 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-amber-900">
            {data.count} unmatched order{data.count === 1 ? "" : "s"} in the last
            24 hours
          </div>
          <div
            className="mt-0.5 text-xs text-amber-800 truncate"
            data-testid="invoice-reconcile-sample"
          >
            {sample_names}
            {more > 0 ? ` and ${more} more` : ""} · totalling{" "}
            <span className="font-mono-num">
              {fmtMoney(
                (data.orders || []).reduce((a, o) => a + Number(o.total || 0), 0)
              )}
            </span>
            {!data.sync_enabled && (
              <span className="ml-1.5 italic">
                · invoice sync is currently disabled
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={refresh}
          disabled={loading}
          data-testid="invoice-reconcile-refresh"
          className="text-amber-900 hover:bg-amber-100 hover:text-amber-950"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
        <Button
          onClick={retry}
          disabled={retrying || loading}
          data-testid="invoice-reconcile-retry"
          className="bg-amber-700 hover:bg-amber-800 text-white"
          size="sm"
        >
          <FileText className="h-4 w-4 mr-1.5" />
          {retrying ? "Pushing…" : "Push all to Xero"}
        </Button>
        <Link to="/settings#invoice-sync-settings">
          <Button
            variant="outline"
            size="sm"
            className="border-amber-300 hover:bg-amber-100 text-amber-900"
            data-testid="invoice-reconcile-settings"
          >
            Settings <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
